import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import { Button } from '@resource-kit/ui/react';
import { hasDispatchWindow } from '../board/flowCopy';
import { formatBatchDay } from '../board/formatBatchDay';
import { runButtonVariant, timelineGeometry } from '../pending/runCardModel';

/** The "now" pointer and the run label follow the clock at this pace. */
const CLOCK_TICK_MS = 60_000;

const SMALL = 'text-[12.5px] text-muted-foreground';

export interface DispatchRunCardProps {
  /** The batch (work) date, `YYYY-MM-DD`. */
  date: string;
  /** Tenant today, `YYYY-MM-DD`. */
  today: string;
  /** Packaging / dispatch cutoff `HH:mm`; `null` when the delivery flow failed. */
  gates: { packaging: string; dispatch: string } | null;
  /** Delivery jobs a run would send: `plannedTotal − anomalies`. */
  pendReady: number;
  /** Driver tasks a run would create under the current switch. */
  tasks: number;
  /** Same-address stops that merge under consolidation. */
  mergeableStops: number;
  consolidate: boolean;
  onConsolidateChange: (value: boolean) => void;
  /** The dry-run has not answered for this date. */
  disabled: boolean;
  running: boolean;
  runProgress: { dispatched: number; skipped: number; remaining: number } | null;
  onRun: () => void;
}

/**
 * The full-width bar on top of the awaiting-dispatch pane (#2475, design
 * `.box`): batch + dispatch gate, then one row (stacked below `lg`) of the
 * driver-task count, the consolidate switch, the gate timeline and the run
 * button, then progress and what happens to orders that arrive later.
 */
export function DispatchRunCard({
  date,
  today,
  gates,
  pendReady,
  tasks,
  mergeableStops,
  consolidate,
  onConsolidateChange,
  disabled,
  running,
  runProgress,
  onRun,
}: DispatchRunCardProps) {
  const { t, i18n } = useTranslation();
  const [nowMs, setNowMs] = useState(Date.now);
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const isToday = date === today;
  const batch = formatBatchDay(date, i18n.language);
  // No ordering rule between the two cutoffs (#2631): a span only when packaging is first.
  const windowed = gates !== null && hasDispatchWindow(gates.packaging, gates.dispatch);
  let gateLine = batch;
  if (gates) {
    gateLine = windowed
      ? t(isToday ? 'dispatch.runCard.gate' : 'dispatch.runCard.gateOtherDay', {
          batch,
          from: gates.packaging,
          to: gates.dispatch,
        })
      : t(isToday ? 'dispatch.runCard.gateCutoffOnly' : 'dispatch.runCard.gateCutoffOnlyOtherDay', {
          batch,
          at: gates.dispatch,
        });
  }
  const variant = runButtonVariant({
    date,
    today,
    nowMs,
    packagingCutoff: windowed ? (gates?.packaging ?? null) : null,
  });
  const geometry = gates
    ? timelineGeometry({
        date,
        isToday,
        nowMs,
        packagingCutoff: windowed ? gates.packaging : null,
        dispatchCutoff: gates.dispatch,
      })
    : null;
  const mergeNow = t('dispatch.runCard.mergeNow', { count: mergeableStops });

  const runLabel = running
    ? runProgress
      ? t('dispatch.dispatchingProgress', { dispatched: runProgress.dispatched })
      : t('dispatch.dispatching')
    : variant === 'beforeCutoff'
      ? t('dispatch.runCard.runBeforeCutoff', { at: gates?.packaging })
      : variant === 'early'
        ? t('dispatch.runCard.runEarly')
        : t('dispatch.runCard.run');
  const runProcessed = runProgress ? runProgress.dispatched + runProgress.skipped : 0;
  const runTotal = runProgress ? runProcessed + runProgress.remaining : 0;
  const runPercent = runTotal > 0 ? Math.round((runProcessed / runTotal) * 100) : 0;

  return (
    <div
      data-testid="dispatch-run-bar"
      className="flex flex-col gap-3 rounded-xl bg-surface-1 p-4 outline outline-1 -outline-offset-1 outline-border"
    >
      <div data-testid="dispatch-run-gate" className={SMALL}>
        {gateLine}
      </div>
      <div
        data-testid="dispatch-run-row"
        className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-5"
      >
        <div className="flex items-baseline gap-2">
          <span
            data-testid="dispatch-run-tasks"
            className="text-[38px] font-display font-bold leading-none tracking-[-0.03em]"
          >
            {tasks}
          </span>
          <span className={SMALL}>
            {t('dispatch.runCard.tasksUnit', { count: tasks })} ·{' '}
            {t('dispatch.runCard.fromJobs', { count: pendReady })}
          </span>
        </div>
        <div className="flex items-center gap-2.5 text-[13px]">
          <button
            type="button"
            role="switch"
            data-testid="dispatch-consolidate"
            aria-checked={consolidate}
            aria-label={t('dispatch.consolidate.label')}
            disabled={running}
            onClick={() => onConsolidateChange(!consolidate)}
            className={cn(
              'relative h-5 w-9 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              consolidate ? 'bg-primary' : 'bg-input',
            )}
          >
            <span
              aria-hidden
              // Off knob uses muted-foreground: it stays visible on bg-input in both themes.
              className={cn(
                'absolute top-0.5 h-4 w-4 rounded-full transition-[left]',
                consolidate ? 'left-[18px] bg-primary-foreground' : 'left-0.5 bg-muted-foreground',
              )}
            />
          </button>
          <span>{t('dispatch.consolidate.label')}</span>
          <span data-testid="dispatch-run-merge-note" className={SMALL}>
            · {consolidate ? mergeNow : t('dispatch.runCard.mergeOff')}
          </span>
        </div>
        {gates && geometry ? (
          <div
            data-testid="dispatch-run-timeline"
            className="rounded-lg bg-muted p-2.5 lg:min-w-0 lg:flex-1"
          >
            <div className="relative h-20">
              <div className="absolute inset-x-0 bottom-2 h-px bg-border" />
              {windowed && geometry.packagingPct !== null ? (
                <div
                  className="absolute inset-y-0 w-0 border-l-[1.5px] border-warning"
                  style={{ left: `${geometry.packagingPct}%` }}
                />
              ) : null}
              <div className="absolute inset-y-0 right-0 w-0 border-l-[1.5px] border-dashed border-destructive" />
              {geometry.pointerPct !== null ? (
                <div
                  data-testid="dispatch-run-pointer"
                  className="absolute inset-y-0"
                  style={{ left: `${geometry.pointerPct}%` }}
                >
                  <div className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-primary" />
                  <span
                    className={cn(
                      'absolute top-0 whitespace-nowrap text-[11.5px] text-muted-foreground',
                      geometry.pointerPct > 50 ? 'right-1.5' : 'left-1.5',
                    )}
                  >
                    {mergeNow}
                  </span>
                </div>
              ) : null}
            </div>
            <div className={cn('mt-1 flex justify-between', SMALL)}>
              <span>{geometry.startLabel}</span>
              {windowed ? (
                <span className="text-warning">
                  {t('dispatch.runCard.timelinePackaging', { at: gates.packaging })}
                </span>
              ) : null}
              <span
                className="text-destructive"
                aria-label={t('dispatch.board.gate.dispatch_cutoff', { at: gates.dispatch })}
              >
                {gates.dispatch}
              </span>
            </div>
          </div>
        ) : null}
        <Button
          data-testid="dispatch-run"
          onClick={onRun}
          disabled={disabled || running || pendReady === 0}
          className="h-11 w-full justify-center rounded-lg text-[14.5px] lg:ml-auto lg:w-auto lg:shrink-0"
        >
          {runLabel}
        </Button>
      </div>
      {running && runProgress ? (
        <div data-testid="dispatch-run-progress" className="w-full space-y-1">
          <div
            role="progressbar"
            aria-valuenow={runPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-2 w-full overflow-hidden rounded-full bg-muted"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${runPercent}%` }}
            />
          </div>
          <div className="text-xs text-muted-foreground">
            {t('dispatch.runProgressCount', {
              processed: runProcessed,
              total: runTotal,
              remaining: runProgress.remaining,
            })}
          </div>
        </div>
      ) : null}
      <div className={SMALL}>{t('dispatch.runCard.note')}</div>
    </div>
  );
}
